import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { DynamicDataSheetGrid, createTextColumn, keyColumn } from "react-datasheet-grid";
import type { CellProps, Column } from "react-datasheet-grid";
import "react-datasheet-grid/dist/style.css";
import type { ColumnDataType, ColumnDefinition, ColumnRole, DataRow } from "../../schema/chart";
import { DataChart } from "./DataChart";
import { formatValue, toNumber } from "./formatValue";
import { parseTSV } from "./parseTSV";
import { useTableExport } from "./useTableExport";

// ---------------------------------------------------------------------------
// Shared helpers & constants
// ---------------------------------------------------------------------------

const stringColumn = createTextColumn<string>({
    deletedValue: "",
    parseUserInput: (v) => v,
    formatBlurredInput: (v) => v ?? "",
    formatInputOnFocus: (v) => v ?? "",
});

const ROLE_LABELS: Record<ColumnRole, string> = {
    primary_key: "Primary Key",
    data: "Data",
    display: "Display",
};

const DATA_TYPE_LABELS: Record<ColumnDataType, string> = {
    string: "String",
    number: "Number",
    boolean: "Boolean",
    date_epoch: "Date (Epoch)",
    date_iso: "Date (ISO)",
    percentage: "Percentage",
    currency: "Currency",
    duration: "Duration",
    url: "URL",
    email: "Email",
    image_url: "Image URL",
    color: "Color",
    rating: "Rating",
};

const TYPES_BY_ROLE: Record<ColumnRole, ColumnDataType[]> = {
    primary_key: ["string", "date_iso", "date_epoch", "number"],
    data: ["number", "percentage", "currency", "duration", "rating", "date_epoch"],
    display: [
        "string",
        "boolean",
        "url",
        "email",
        "image_url",
        "color",
        "number",
        "date_iso",
        "date_epoch",
        "percentage",
        "currency",
        "duration",
        "rating",
    ],
};

function defaultTypeForRole(role: ColumnRole): ColumnDataType {
    switch (role) {
        case "primary_key":
            return "string";
        case "data":
            return "number";
        case "display":
            return "string";
    }
}

// ---------------------------------------------------------------------------
// Filter / Sort types
// ---------------------------------------------------------------------------

type NumberFilter = { type: "number"; min?: number; max?: number };
type TextFilter = { type: "text"; query: string };
type DateFilter = { type: "date"; from?: string; to?: string };
type ColumnFilter = NumberFilter | TextFilter | DateFilter;

type SortDirection = "asc" | "desc";
type SortEntry = { column: string; direction: SortDirection };

function filterTypeForDataType(dt: ColumnDataType): ColumnFilter["type"] {
    switch (dt) {
        case "number":
        case "currency":
        case "percentage":
        case "duration":
        case "rating":
            return "number";
        case "date_epoch":
        case "date_iso":
            return "date";
        default:
            return "text";
    }
}

// ---------------------------------------------------------------------------
// Data pipeline helpers
// ---------------------------------------------------------------------------

function applySearch(rows: DataRow[], query: string): DataRow[] {
    if (!query) return rows;
    const lower = query.toLowerCase();
    return rows.filter((row) => Object.values(row).some((v) => v.toLowerCase().includes(lower)));
}

function parseDateToTimestamp(raw: string, dataType: ColumnDataType): number {
    if (dataType === "date_epoch") {
        const n = toNumber(raw);
        return n < 1e10 ? n * 1000 : n;
    }
    return new Date(raw).getTime();
}

function applyColumnFilters(
    rows: DataRow[],
    filters: Map<string, ColumnFilter>,
    columns: ColumnDefinition[],
): DataRow[] {
    if (filters.size === 0) return rows;
    return rows.filter((row) => {
        for (const [colName, filter] of filters) {
            const val = row[colName] ?? "";
            const colDef = columns.find((c) => c.name === colName);
            if (!colDef) continue;
            switch (filter.type) {
                case "number": {
                    const n = toNumber(val);
                    if (filter.min !== undefined && n < filter.min) return false;
                    if (filter.max !== undefined && n > filter.max) return false;
                    break;
                }
                case "text": {
                    if (!val.toLowerCase().includes(filter.query.toLowerCase())) return false;
                    break;
                }
                case "date": {
                    const ts = parseDateToTimestamp(val, colDef.dataType);
                    if (isNaN(ts)) return false;
                    if (filter.from) {
                        const fromTs = new Date(filter.from).getTime();
                        if (!isNaN(fromTs) && ts < fromTs) return false;
                    }
                    if (filter.to) {
                        const toTs = new Date(filter.to).getTime() + 86400000 - 1;
                        if (!isNaN(toTs) && ts > toTs) return false;
                    }
                    break;
                }
            }
        }
        return true;
    });
}

const NUMERIC_SORT_TYPES: Set<ColumnDataType> = new Set([
    "number",
    "currency",
    "percentage",
    "duration",
    "rating",
    "date_epoch",
]);

function applyMultiSort(
    rows: DataRow[],
    sorts: SortEntry[],
    columns: ColumnDefinition[],
): DataRow[] {
    if (sorts.length === 0) return rows;
    const sorted = [...rows];
    sorted.sort((a, b) => {
        for (const { column, direction } of sorts) {
            const colDef = columns.find((c) => c.name === column);
            if (!colDef) continue;
            const av = a[column] ?? "";
            const bv = b[column] ?? "";
            const mul = direction === "asc" ? 1 : -1;
            let cmp: number;
            if (NUMERIC_SORT_TYPES.has(colDef.dataType)) {
                cmp = toNumber(av) - toNumber(bv);
            } else if (colDef.dataType === "date_iso") {
                cmp = new Date(av).getTime() - new Date(bv).getTime();
            } else {
                cmp = av.localeCompare(bv);
            }
            if (cmp !== 0) return cmp * mul;
        }
        return 0;
    });
    return sorted;
}

function useDebouncedValue(value: string, delay: number): string {
    const [debounced, setDebounced] = useState(value);
    useEffect(() => {
        const timer = setTimeout(() => setDebounced(value), delay);
        return () => clearTimeout(timer);
    }, [value, delay]);
    return debounced;
}

// ---------------------------------------------------------------------------
// SVG icons
// ---------------------------------------------------------------------------

const ChevronDownIcon: React.FC<{ size?: number }> = ({ size = 14 }) => (
    <svg
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth={2}
        strokeLinecap="round"
        strokeLinejoin="round"
    >
        <polyline points="6 9 12 15 18 9" />
    </svg>
);

const SortAscIcon: React.FC<{ size?: number }> = ({ size = 14 }) => (
    <svg
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth={2}
        strokeLinecap="round"
        strokeLinejoin="round"
    >
        <polyline points="12 19 12 5" />
        <polyline points="5 12 12 5 19 12" />
    </svg>
);

const SortDescIcon: React.FC<{ size?: number }> = ({ size = 14 }) => (
    <svg
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth={2}
        strokeLinecap="round"
        strokeLinejoin="round"
    >
        <polyline points="12 5 12 19" />
        <polyline points="19 12 12 19 5 12" />
    </svg>
);

const FilterIcon: React.FC<{ size?: number }> = ({ size = 14 }) => (
    <svg
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth={2}
        strokeLinecap="round"
        strokeLinejoin="round"
    >
        <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
    </svg>
);

const SearchIcon: React.FC<{ size?: number }> = ({ size = 14 }) => (
    <svg
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth={2}
        strokeLinecap="round"
        strokeLinejoin="round"
    >
        <circle cx="11" cy="11" r="8" />
        <line x1="21" y1="21" x2="16.65" y2="16.65" />
    </svg>
);

// ---------------------------------------------------------------------------
// Edit-mode sub-components (unchanged)
// ---------------------------------------------------------------------------

const AddRowsWithColumn: React.FC<{
    addRows: (count?: number) => void;
    onAddColumn: () => void;
    exportColumns?: ColumnDefinition[];
    exportRows?: DataRow[];
    exportFilename?: string;
}> = ({ addRows, onAddColumn, exportColumns, exportRows, exportFilename }) => {
    const [value, setValue] = useState(1);
    const [rawValue, setRawValue] = useState("1");

    return (
        <div className="dsg-add-row flex items-center gap-2">
            <div className="flex items-center">
                <button type="button" className="dsg-add-row-btn" onClick={() => addRows(value)}>
                    Add
                </button>{" "}
                <input
                    className="dsg-add-row-input mr-2"
                    value={rawValue}
                    onBlur={() => setRawValue(String(value))}
                    type="number"
                    min={1}
                    onChange={(e) => {
                        setRawValue(e.target.value);
                        setValue(Math.max(1, Math.round(parseInt(e.target.value) || 0)));
                    }}
                    onKeyDown={(e) => {
                        if (e.key === "Enter") addRows(value);
                    }}
                />{" "}
                rows
            </div>
            <span className="text-gray-300">|</span>
            <button
                type="button"
                onClick={onAddColumn}
                className="cursor-pointer border-none bg-transparent px-1 py-0.5 text-sm text-gray-600 hover:text-gray-900"
            >
                + Column
            </button>
            {exportFilename && exportColumns && exportRows && exportRows.length > 0 && (
                <>
                    <span className="text-gray-300">|</span>
                    <ExportDropdown
                        columns={exportColumns}
                        rows={exportRows}
                        filename={exportFilename}
                    />
                </>
            )}
        </div>
    );
};

/** Manages its own local state so renaming doesn't remount the grid on every keystroke. */
const ColumnHeaderInput: React.FC<{
    name: string;
    onCommit: (newName: string) => void;
}> = ({ name, onCommit }) => {
    const [localName, setLocalName] = useState(name);
    const inputRef = useRef<HTMLInputElement>(null);

    // Sync when parent changes (e.g. after a different column is removed)
    useEffect(() => {
        setLocalName(name);
    }, [name]);

    const commit = () => {
        const trimmed = localName.trim();
        if (trimmed && trimmed !== name) {
            onCommit(trimmed);
        } else {
            setLocalName(name);
        }
    };

    return (
        <input
            ref={inputRef}
            value={localName}
            onChange={(e) => setLocalName(e.target.value)}
            onBlur={commit}
            onKeyDown={(e) => {
                if (e.key === "Enter") {
                    e.preventDefault();
                    inputRef.current?.blur();
                }
                if (e.key === "Escape") {
                    setLocalName(name);
                    inputRef.current?.blur();
                }
                e.stopPropagation();
            }}
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
            onFocus={(e) => e.stopPropagation()}
            className="w-full min-w-0 border-none bg-transparent p-0 text-[13px] font-semibold outline-hidden"
        />
    );
};

const ColumnHeaderMenu: React.FC<{
    col: ColumnDefinition;
    colIdx: number;
    colCount: number;
    canRemove: boolean;
    onChangeRole: (colIdx: number, role: ColumnRole) => void;
    onChangeDataType: (colIdx: number, dataType: ColumnDataType) => void;
    onRemove: (colIdx: number) => void;
    onMove: (colIdx: number, direction: "left" | "right") => void;
}> = ({ col, colIdx, colCount, canRemove, onChangeRole, onChangeDataType, onRemove, onMove }) => {
    const [open, setOpen] = useState(false);
    const buttonRef = useRef<HTMLButtonElement>(null);
    const dropdownRef = useRef<HTMLDivElement>(null);
    const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0 });

    useEffect(() => {
        if (!open) return;
        const handleClick = (e: MouseEvent) => {
            if (
                buttonRef.current?.contains(e.target as Node) ||
                dropdownRef.current?.contains(e.target as Node)
            )
                return;
            setOpen(false);
        };
        document.addEventListener("mousedown", handleClick);
        return () => document.removeEventListener("mousedown", handleClick);
    }, [open]);

    useEffect(() => {
        if (open && buttonRef.current) {
            const rect = buttonRef.current.getBoundingClientRect();
            const menuWidth = 160;
            let left = rect.right - menuWidth;
            if (left < 8) left = 8;
            setDropdownPos({ top: rect.bottom + 4, left });
        }
    }, [open]);

    const compatibleTypes = TYPES_BY_ROLE[col.role];

    return (
        <div className="relative shrink-0">
            <button
                ref={buttonRef}
                onClick={(e) => {
                    e.stopPropagation();
                    setOpen((o) => !o);
                }}
                title="Column options"
                className="flex cursor-pointer items-center border-none bg-transparent px-0.5 leading-none text-gray-400"
            >
                <ChevronDownIcon size={14} />
            </button>
            {open &&
                createPortal(
                    <div
                        ref={dropdownRef}
                        className="fixed z-9999 max-h-[400px] min-w-[160px] overflow-y-auto rounded-lg border border-gray-200 bg-white py-1 text-[13px] shadow-lg"
                        style={{ top: dropdownPos.top, left: dropdownPos.left }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        {/* Role section */}
                        <div className="px-3 py-1 text-[11px] font-semibold tracking-wider text-gray-400 uppercase">
                            Column Role
                        </div>
                        {(["primary_key", "data", "display"] as ColumnRole[]).map((role) => (
                            <MenuOption
                                key={role}
                                label={ROLE_LABELS[role]}
                                selected={col.role === role}
                                onClick={() => {
                                    onChangeRole(colIdx, role);
                                    setOpen(false);
                                }}
                            />
                        ))}

                        <div className="my-1 h-px bg-gray-200" />

                        {/* Data Type section */}
                        <div className="px-3 py-1 text-[11px] font-semibold tracking-wider text-gray-400 uppercase">
                            Data Type
                        </div>
                        {compatibleTypes.map((dt) => (
                            <MenuOption
                                key={dt}
                                label={DATA_TYPE_LABELS[dt]}
                                selected={col.dataType === dt}
                                onClick={() => {
                                    onChangeDataType(colIdx, dt);
                                    setOpen(false);
                                }}
                            />
                        ))}

                        {colCount > 1 && (
                            <>
                                <div className="my-1 h-px bg-gray-200" />
                                <div className="px-3 py-1 text-[11px] font-semibold tracking-wider text-gray-400 uppercase">
                                    Reorder
                                </div>
                                <button
                                    onClick={() => {
                                        onMove(colIdx, "left");
                                        setOpen(false);
                                    }}
                                    disabled={colIdx === 0}
                                    className={`flex w-full items-center gap-2 border-none bg-transparent px-3 py-1.5 text-left text-[13px] ${
                                        colIdx === 0
                                            ? "cursor-default text-gray-300"
                                            : "cursor-pointer text-gray-700 hover:bg-gray-50"
                                    }`}
                                >
                                    <svg
                                        width={14}
                                        height={14}
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth={2}
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    >
                                        <polyline points="15 18 9 12 15 6" />
                                    </svg>
                                    Move Left
                                </button>
                                <button
                                    onClick={() => {
                                        onMove(colIdx, "right");
                                        setOpen(false);
                                    }}
                                    disabled={colIdx === colCount - 1}
                                    className={`flex w-full items-center gap-2 border-none bg-transparent px-3 py-1.5 text-left text-[13px] ${
                                        colIdx === colCount - 1
                                            ? "cursor-default text-gray-300"
                                            : "cursor-pointer text-gray-700 hover:bg-gray-50"
                                    }`}
                                >
                                    <svg
                                        width={14}
                                        height={14}
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth={2}
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    >
                                        <polyline points="9 18 15 12 9 6" />
                                    </svg>
                                    Move Right
                                </button>
                            </>
                        )}

                        {canRemove && (
                            <>
                                <div className="my-1 h-px bg-gray-200" />
                                <button
                                    onClick={() => {
                                        onRemove(colIdx);
                                        setOpen(false);
                                    }}
                                    className="flex w-full cursor-pointer items-center gap-2 border-none bg-transparent px-3 py-1.5 text-left text-[13px] text-red-500 hover:bg-red-50"
                                >
                                    <svg
                                        width={14}
                                        height={14}
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth={2}
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    >
                                        <polyline points="3 6 5 6 21 6" />
                                        <path d="M19 6l-1 14H6L5 6" />
                                        <path d="M10 11v6" />
                                        <path d="M14 11v6" />
                                        <path d="M9 6V4h6v2" />
                                    </svg>
                                    Delete Column
                                </button>
                            </>
                        )}
                    </div>,
                    document.body,
                )}
        </div>
    );
};

const MenuOption: React.FC<{
    label: string;
    selected: boolean;
    onClick: () => void;
}> = ({ label, selected, onClick }) => (
    <button
        onClick={onClick}
        className={`flex w-full cursor-pointer items-center gap-2 border-none px-3 py-1.5 text-left text-[13px] ${
            selected
                ? "bg-sky-50 font-semibold text-indigo-600"
                : "bg-transparent font-normal text-gray-700 hover:bg-gray-50"
        }`}
    >
        {selected ? (
            <svg
                width={14}
                height={14}
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth={2.5}
                strokeLinecap="round"
                strokeLinejoin="round"
            >
                <polyline points="20 6 9 17 4 12" />
            </svg>
        ) : (
            <span className="w-3.5" />
        )}
        {label}
    </button>
);

const CellRenderer: React.FC<{ value: string; col: ColumnDefinition }> = ({ value, col }) => {
    if (!value) return null;

    switch (col.dataType) {
        case "url":
            return (
                <a
                    href={value}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-indigo-600 underline"
                    onClick={(e) => e.stopPropagation()}
                >
                    {value}
                </a>
            );
        case "email":
            return (
                <a
                    href={`mailto:${value}`}
                    className="text-indigo-600 underline"
                    onClick={(e) => e.stopPropagation()}
                >
                    {value}
                </a>
            );
        case "image_url":
            return (
                <img
                    src={value}
                    alt=""
                    className="max-h-6 max-w-[60px] object-contain align-middle"
                />
            );
        case "color":
            return (
                <span className="inline-flex items-center gap-1">
                    <span
                        className="inline-block h-3.5 w-3.5 rounded-sm border border-gray-300 align-middle"
                        style={{ backgroundColor: value }}
                    />
                    {value}
                </span>
            );
        case "boolean": {
            const isTrue = ["true", "t", "yes", "y", "1"].includes(value.toLowerCase());
            return (
                <span
                    className={`inline-block rounded px-1.5 py-px text-xs font-medium ${
                        isTrue ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"
                    }`}
                >
                    {isTrue ? "Yes" : "No"}
                </span>
            );
        }
        case "rating":
            return <span>{formatValue(value, "rating")}</span>;
        default:
            return <span>{formatValue(value, col.dataType)}</span>;
    }
};

const ExportDropdown: React.FC<{
    columns: ColumnDefinition[];
    rows: DataRow[];
    filename: string;
}> = ({ columns, rows, filename }) => {
    const [open, setOpen] = useState(false);
    const ref = useRef<HTMLDivElement>(null);
    const { exportCSV, exportExcel } = useTableExport();

    useEffect(() => {
        if (!open) return;
        const handleClick = (e: MouseEvent) => {
            if (ref.current && !ref.current.contains(e.target as Node)) {
                setOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClick);
        return () => document.removeEventListener("mousedown", handleClick);
    }, [open]);

    return (
        <div ref={ref} className="relative inline-block">
            <button
                onClick={() => setOpen((o) => !o)}
                className="inline-flex cursor-pointer items-center gap-1 rounded border border-gray-300 bg-white px-3 py-1.5 text-[13px] hover:bg-gray-50"
            >
                Export
                <ChevronDownIcon size={12} />
            </button>
            {open && (
                <div className="absolute top-full left-0 z-50 mt-1 min-w-[120px] rounded-lg border border-gray-200 bg-white py-1 text-[13px] shadow-lg">
                    <button
                        onClick={() => {
                            exportCSV(columns, rows, filename);
                            setOpen(false);
                        }}
                        className="block w-full cursor-pointer border-none bg-transparent px-3 py-1.5 text-left text-[13px] hover:bg-gray-50"
                    >
                        CSV (.csv)
                    </button>
                    <button
                        onClick={() => {
                            exportExcel(columns, rows, filename);
                            setOpen(false);
                        }}
                        className="block w-full cursor-pointer border-none bg-transparent px-3 py-1.5 text-left text-[13px] hover:bg-gray-50"
                    >
                        Excel (.xlsx)
                    </button>
                </div>
            )}
        </div>
    );
};

// ---------------------------------------------------------------------------
// View-mode toggle (Table / Line / Bar)
// ---------------------------------------------------------------------------

type ViewMode = "table" | "line" | "bar";

const ViewModeToggle: React.FC<{
    viewMode: ViewMode;
    onChange: (mode: ViewMode) => void;
}> = ({ viewMode, onChange }) => {
    const base =
        "inline-flex cursor-pointer items-center gap-1 border border-gray-300 px-3 py-1.5 text-[13px]";
    const active = "bg-gray-100 border-gray-400";
    const inactive = "bg-white hover:bg-gray-50";

    return (
        <div className="inline-flex overflow-hidden rounded">
            <button
                onClick={() => onChange("table")}
                className={`${base} rounded-l ${viewMode === "table" ? active : inactive}`}
                title="Table view"
            >
                <svg
                    width="14"
                    height="14"
                    viewBox="0 0 14 14"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.5"
                >
                    <rect x="1" y="1" width="12" height="12" rx="1" />
                    <line x1="1" y1="5" x2="13" y2="5" />
                    <line x1="1" y1="9" x2="13" y2="9" />
                    <line x1="5" y1="1" x2="5" y2="13" />
                    <line x1="9" y1="1" x2="9" y2="13" />
                </svg>
                Table
            </button>
            <button
                onClick={() => onChange("line")}
                className={`${base} border-l-0 ${viewMode === "line" ? active : inactive}`}
                title="Line chart view"
            >
                <svg
                    width="14"
                    height="14"
                    viewBox="0 0 14 14"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.5"
                >
                    <polyline points="1,11 4,6 8,8 13,2" />
                </svg>
                Line
            </button>
            <button
                onClick={() => onChange("bar")}
                className={`${base} rounded-r border-l-0 ${viewMode === "bar" ? active : inactive}`}
                title="Bar chart view"
            >
                <svg
                    width="14"
                    height="14"
                    viewBox="0 0 14 14"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.5"
                >
                    <rect x="1" y="8" width="3" height="5" />
                    <rect x="5.5" y="4" width="3" height="9" />
                    <rect x="10" y="1" width="3" height="12" />
                </svg>
                Bar
            </button>
        </div>
    );
};

// ---------------------------------------------------------------------------
// Read-only column header dropdown (sort + filter per column)
// ---------------------------------------------------------------------------

const ReadOnlyColumnHeaderMenu: React.FC<{
    col: ColumnDefinition;
    sortDirection: SortDirection | null;
    sortIndex: number | null;
    onSort: (direction: SortDirection | null) => void;
    filter: ColumnFilter | null;
    onFilterChange: (filter: ColumnFilter | null) => void;
}> = ({ col, sortDirection, sortIndex, onSort, filter, onFilterChange }) => {
    const [open, setOpen] = useState(false);
    const triggerRef = useRef<HTMLButtonElement>(null);
    const panelRef = useRef<HTMLDivElement>(null);
    const [pos, setPos] = useState<{ top: number; left: number }>({ top: 0, left: 0 });

    // Local filter editing state
    const ft = filterTypeForDataType(col.dataType);
    const [numMin, setNumMin] = useState("");
    const [numMax, setNumMax] = useState("");
    const [textQuery, setTextQuery] = useState("");
    const [dateFrom, setDateFrom] = useState("");
    const [dateTo, setDateTo] = useState("");

    // Sync local state when filter changes externally (e.g. clear all)
    useEffect(() => {
        if (!filter) {
            setNumMin("");
            setNumMax("");
            setTextQuery("");
            setDateFrom("");
            setDateTo("");
        } else if (filter.type === "number") {
            setNumMin(filter.min !== undefined ? String(filter.min) : "");
            setNumMax(filter.max !== undefined ? String(filter.max) : "");
        } else if (filter.type === "text") {
            setTextQuery(filter.query);
        } else if (filter.type === "date") {
            setDateFrom(filter.from ?? "");
            setDateTo(filter.to ?? "");
        }
    }, [filter]);

    // Position the portal panel below the trigger button
    useEffect(() => {
        if (!open || !triggerRef.current) return;
        const rect = triggerRef.current.getBoundingClientRect();
        setPos({
            top: rect.bottom + 4,
            left: Math.max(0, rect.right - 200), // align right edge, min-width 200
        });
    }, [open]);

    // Close on outside click (check both trigger and portal panel)
    useEffect(() => {
        if (!open) return;
        const handleClick = (e: MouseEvent) => {
            const target = e.target as Node;
            if (triggerRef.current?.contains(target) || panelRef.current?.contains(target)) {
                return;
            }
            setOpen(false);
        };
        document.addEventListener("mousedown", handleClick);
        return () => document.removeEventListener("mousedown", handleClick);
    }, [open]);

    const applyFilter = () => {
        switch (ft) {
            case "number": {
                const min = numMin !== "" ? Number(numMin) : undefined;
                const max = numMax !== "" ? Number(numMax) : undefined;
                if (min !== undefined || max !== undefined) {
                    onFilterChange({ type: "number", min, max });
                } else {
                    onFilterChange(null);
                }
                break;
            }
            case "text": {
                if (textQuery.trim()) {
                    onFilterChange({ type: "text", query: textQuery.trim() });
                } else {
                    onFilterChange(null);
                }
                break;
            }
            case "date": {
                if (dateFrom || dateTo) {
                    onFilterChange({
                        type: "date",
                        from: dateFrom || undefined,
                        to: dateTo || undefined,
                    });
                } else {
                    onFilterChange(null);
                }
                break;
            }
        }
        setOpen(false);
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === "Enter") applyFilter();
        e.stopPropagation();
    };

    const hasActiveSort = sortDirection !== null;
    const hasActiveFilter = filter !== null;

    return (
        <span className="shrink-0">
            <button
                ref={triggerRef}
                onClick={(e) => {
                    e.stopPropagation();
                    setOpen((o) => !o);
                }}
                title="Sort & Filter"
                className={`flex cursor-pointer items-center border-none bg-transparent px-0.5 leading-none ${
                    hasActiveSort || hasActiveFilter ? "text-indigo-500" : "text-gray-400"
                }`}
            >
                <ChevronDownIcon size={14} />
            </button>
            {open &&
                createPortal(
                    <div
                        ref={panelRef}
                        className="max-h-[400px] min-w-[200px] overflow-y-auto rounded-lg border border-gray-200 bg-white py-1 text-[13px] shadow-lg"
                        style={{
                            position: "fixed",
                            top: pos.top,
                            left: pos.left,
                            zIndex: 9999,
                        }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        {/* Sort section */}
                        <div className="px-3 py-1 text-[11px] font-semibold tracking-wider text-gray-400 uppercase">
                            Sort
                        </div>
                        <button
                            onClick={() => {
                                onSort(sortDirection === "asc" ? null : "asc");
                                setOpen(false);
                            }}
                            className={`flex w-full cursor-pointer items-center gap-2 border-none px-3 py-1.5 text-left text-[13px] ${
                                sortDirection === "asc"
                                    ? "bg-sky-50 font-semibold text-indigo-600"
                                    : "bg-transparent text-gray-700 hover:bg-gray-50"
                            }`}
                        >
                            <SortAscIcon size={14} />
                            Ascending
                            {sortDirection === "asc" && sortIndex !== null && sortIndex > 0 && (
                                <span className="ml-auto text-[11px] text-gray-400">
                                    #{sortIndex + 1}
                                </span>
                            )}
                        </button>
                        <button
                            onClick={() => {
                                onSort(sortDirection === "desc" ? null : "desc");
                                setOpen(false);
                            }}
                            className={`flex w-full cursor-pointer items-center gap-2 border-none px-3 py-1.5 text-left text-[13px] ${
                                sortDirection === "desc"
                                    ? "bg-sky-50 font-semibold text-indigo-600"
                                    : "bg-transparent text-gray-700 hover:bg-gray-50"
                            }`}
                        >
                            <SortDescIcon size={14} />
                            Descending
                            {sortDirection === "desc" && sortIndex !== null && sortIndex > 0 && (
                                <span className="ml-auto text-[11px] text-gray-400">
                                    #{sortIndex + 1}
                                </span>
                            )}
                        </button>

                        <div className="my-1 h-px bg-gray-200" />

                        {/* Filter section */}
                        <div className="px-3 py-1 text-[11px] font-semibold tracking-wider text-gray-400 uppercase">
                            Filter
                        </div>
                        <div className="px-3 py-1.5">
                            {ft === "number" && (
                                <div className="flex items-center gap-1.5">
                                    <input
                                        type="number"
                                        placeholder="Min"
                                        value={numMin}
                                        onChange={(e) => setNumMin(e.target.value)}
                                        onKeyDown={handleKeyDown}
                                        className="w-20 rounded border border-gray-300 px-2 py-1 text-[13px] outline-hidden focus:border-indigo-400"
                                    />
                                    <span className="text-gray-400">&ndash;</span>
                                    <input
                                        type="number"
                                        placeholder="Max"
                                        value={numMax}
                                        onChange={(e) => setNumMax(e.target.value)}
                                        onKeyDown={handleKeyDown}
                                        className="w-20 rounded border border-gray-300 px-2 py-1 text-[13px] outline-hidden focus:border-indigo-400"
                                    />
                                </div>
                            )}
                            {ft === "text" && (
                                <input
                                    type="text"
                                    placeholder="Contains..."
                                    value={textQuery}
                                    onChange={(e) => setTextQuery(e.target.value)}
                                    onKeyDown={handleKeyDown}
                                    autoFocus
                                    className="w-full rounded border border-gray-300 px-2 py-1 text-[13px] outline-hidden focus:border-indigo-400"
                                />
                            )}
                            {ft === "date" && (
                                <div className="flex items-center gap-1.5">
                                    <input
                                        type="date"
                                        value={dateFrom}
                                        onChange={(e) => setDateFrom(e.target.value)}
                                        onKeyDown={handleKeyDown}
                                        className="rounded border border-gray-300 px-2 py-1 text-[13px] outline-hidden focus:border-indigo-400"
                                    />
                                    <span className="text-gray-400">&ndash;</span>
                                    <input
                                        type="date"
                                        value={dateTo}
                                        onChange={(e) => setDateTo(e.target.value)}
                                        onKeyDown={handleKeyDown}
                                        className="rounded border border-gray-300 px-2 py-1 text-[13px] outline-hidden focus:border-indigo-400"
                                    />
                                </div>
                            )}
                            <div className="mt-2 flex items-center gap-2">
                                <button
                                    onClick={applyFilter}
                                    className="cursor-pointer rounded bg-indigo-500 px-3 py-1 text-[12px] font-medium text-white hover:bg-indigo-600"
                                >
                                    Apply
                                </button>
                                {hasActiveFilter && (
                                    <button
                                        onClick={() => {
                                            onFilterChange(null);
                                            setNumMin("");
                                            setNumMax("");
                                            setTextQuery("");
                                            setDateFrom("");
                                            setDateTo("");
                                            setOpen(false);
                                        }}
                                        className="cursor-pointer rounded border border-gray-300 bg-white px-3 py-1 text-[12px] text-gray-600 hover:bg-gray-50"
                                    >
                                        Clear
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>,
                    document.body,
                )}
        </span>
    );
};

// ---------------------------------------------------------------------------
// Read-only toolbar: search bar + active sort/filter summary chips
// ---------------------------------------------------------------------------

const ReadOnlyToolbar: React.FC<{
    searchQuery: string;
    onSearchChange: (q: string) => void;
    sorts: SortEntry[];
    columnFilters: Map<string, ColumnFilter>;
    onClearAll: () => void;
    filteredCount: number;
    totalCount: number;
}> = ({
    searchQuery,
    onSearchChange,
    sorts,
    columnFilters,
    onClearAll,
    filteredCount,
    totalCount,
}) => {
    const hasAny = searchQuery || sorts.length > 0 || columnFilters.size > 0;
    const showCount = hasAny && filteredCount !== totalCount;

    return (
        <div className="mb-2 flex flex-wrap items-center gap-2 text-[13px]">
            <div className="relative flex-1">
                <span className="pointer-events-none absolute top-1/2 left-2 -translate-y-1/2 text-gray-400">
                    <SearchIcon size={14} />
                </span>
                <input
                    type="text"
                    placeholder="Search..."
                    value={searchQuery}
                    onChange={(e) => onSearchChange(e.target.value)}
                    className="w-full rounded border border-gray-300 py-1.5 pr-3 pl-8 text-[13px] outline-hidden focus:border-indigo-400"
                />
            </div>
            {showCount && (
                <span className="text-[12px] whitespace-nowrap text-gray-400">
                    {filteredCount} of {totalCount} rows
                </span>
            )}
            {hasAny && (
                <button
                    onClick={onClearAll}
                    className="cursor-pointer rounded border border-gray-300 bg-white px-2.5 py-1 text-[12px] whitespace-nowrap text-gray-600 hover:bg-gray-50"
                >
                    Clear All
                </button>
            )}
            {/* Active sort chips */}
            {sorts.map((s, i) => (
                <span
                    key={`sort-${s.column}`}
                    className="inline-flex items-center gap-1 rounded-full border border-indigo-200 bg-indigo-50 px-2.5 py-0.5 text-[12px] whitespace-nowrap text-indigo-700"
                >
                    {s.direction === "asc" ? <SortAscIcon size={11} /> : <SortDescIcon size={11} />}
                    {s.column}
                    {sorts.length > 1 && (
                        <span className="text-[10px] text-indigo-400">#{i + 1}</span>
                    )}
                </span>
            ))}
            {/* Active filter chips */}
            {Array.from(columnFilters.entries()).map(([colName, filter]) => {
                let summary: string;
                if (filter.type === "number") {
                    const parts: string[] = [];
                    if (filter.min !== undefined) parts.push(String(filter.min));
                    parts.push("\u2013");
                    if (filter.max !== undefined) parts.push(String(filter.max));
                    summary = parts.join("");
                } else if (filter.type === "text") {
                    summary = `"${filter.query}"`;
                } else {
                    const parts: string[] = [];
                    if (filter.from) parts.push(filter.from);
                    parts.push("\u2013");
                    if (filter.to) parts.push(filter.to);
                    summary = parts.join("");
                }
                return (
                    <span
                        key={`filter-${colName}`}
                        className="inline-flex items-center gap-1 rounded-full border border-amber-200 bg-amber-50 px-2.5 py-0.5 text-[12px] whitespace-nowrap text-amber-700"
                    >
                        <FilterIcon size={11} />
                        {colName}: {summary}
                    </span>
                );
            })}
        </div>
    );
};

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

interface DataTableProps {
    columns: ColumnDefinition[];
    rows: DataRow[];
    onChange: (columns: ColumnDefinition[], rows: DataRow[]) => void;
    readOnly?: boolean;
    onPasteData?: (columns: ColumnDefinition[], rows: DataRow[]) => void;
    exportFilename?: string;
}

export const DataTable: React.FC<DataTableProps> = ({
    columns,
    rows,
    onChange,
    readOnly = false,
    onPasteData,
    exportFilename,
}) => {
    // View mode toggle (table vs chart) — only used in readOnly mode
    const [viewMode, setViewMode] = useState<ViewMode>("table");

    // Read-only sort/filter/search state
    const [searchQuery, setSearchQuery] = useState("");
    const [columnFilters, setColumnFilters] = useState<Map<string, ColumnFilter>>(() => new Map());
    const [sorts, setSorts] = useState<SortEntry[]>([]);

    const debouncedSearch = useDebouncedValue(searchQuery, 200);

    const clearAll = useCallback(() => {
        setSearchQuery("");
        setColumnFilters(new Map());
        setSorts([]);
    }, []);

    const handleSort = useCallback((colName: string, direction: SortDirection | null) => {
        setSorts((prev) => {
            const without = prev.filter((s) => s.column !== colName);
            if (direction === null) return without;
            return [...without, { column: colName, direction }];
        });
    }, []);

    const handleFilterChange = useCallback((colName: string, filter: ColumnFilter | null) => {
        setColumnFilters((prev) => {
            const next = new Map(prev);
            if (filter === null) {
                next.delete(colName);
            } else {
                next.set(colName, filter);
            }
            return next;
        });
    }, []);

    // Data pipeline (only applied in readOnly mode)
    const processedRows = useMemo(() => {
        if (!readOnly) return rows;
        let result = applySearch(rows, debouncedSearch);
        result = applyColumnFilters(result, columnFilters, columns);
        result = applyMultiSort(result, sorts, columns);
        return result;
    }, [readOnly, rows, debouncedSearch, columnFilters, sorts, columns]);

    const displayRows = readOnly ? processedRows : rows;

    // --- Edit-mode callbacks (unchanged) ---
    const handlePaste = useCallback(
        (e: React.ClipboardEvent<HTMLDivElement>) => {
            if (!onPasteData) return;
            const text = e.clipboardData.getData("text/plain");
            if (!text.includes("\t") && !text.includes("\n")) return;
            if (columns.length > 0 && rows.length > 0) return;
            const result = parseTSV(text);
            if (result) {
                e.preventDefault();
                onPasteData(result.columns, result.rows);
            }
        },
        [onPasteData, columns, rows],
    );

    const updateColumnRole = useCallback(
        (colIndex: number, newRole: ColumnRole) => {
            const currentType = columns[colIndex].dataType;
            const compatible = TYPES_BY_ROLE[newRole];
            const newType = compatible.includes(currentType)
                ? currentType
                : defaultTypeForRole(newRole);

            const newColumns = columns.map((col, i) =>
                i === colIndex ? { ...col, role: newRole, dataType: newType } : col,
            );
            onChange(newColumns, rows);
        },
        [columns, rows, onChange],
    );

    const updateColumnDataType = useCallback(
        (colIndex: number, newDataType: ColumnDataType) => {
            const newColumns = columns.map((col, i) =>
                i === colIndex ? { ...col, dataType: newDataType } : col,
            );
            onChange(newColumns, rows);
        },
        [columns, rows, onChange],
    );

    const updateColumnName = useCallback(
        (colIndex: number, newName: string) => {
            const oldName = columns[colIndex].name;
            if (oldName === newName) return;
            const newColumns = columns.map((col, i) =>
                i === colIndex ? { ...col, name: newName } : col,
            );
            const newRows = rows.map((row) => {
                const newRow: DataRow = {};
                for (const [key, val] of Object.entries(row)) {
                    newRow[key === oldName ? newName : key] = val;
                }
                return newRow;
            });
            onChange(newColumns, newRows);
        },
        [columns, rows, onChange],
    );

    const removeColumn = useCallback(
        (colIndex: number) => {
            if (columns.length <= 1) return;
            const colName = columns[colIndex].name;
            const newColumns = columns.filter((_, i) => i !== colIndex);
            const newRows = rows.map((row) => {
                const newRow: DataRow = {};
                for (const [key, val] of Object.entries(row)) {
                    if (key !== colName) newRow[key] = val;
                }
                return newRow;
            });
            onChange(newColumns, newRows);
        },
        [columns, rows, onChange],
    );

    const moveColumn = useCallback(
        (colIndex: number, direction: "left" | "right") => {
            const targetIndex = direction === "left" ? colIndex - 1 : colIndex + 1;
            if (targetIndex < 0 || targetIndex >= columns.length) return;
            const newColumns = [...columns];
            [newColumns[colIndex], newColumns[targetIndex]] = [
                newColumns[targetIndex],
                newColumns[colIndex],
            ];
            onChange(newColumns, rows);
        },
        [columns, rows, onChange],
    );

    const addColumn = useCallback(() => {
        const name = `Column ${columns.length}`;
        const newCol: ColumnDefinition = { name, role: "data", dataType: "number" };
        const newColumns = [...columns, newCol];
        const newRows = rows.map((row) => ({ ...row, [name]: "" }));
        onChange(newColumns, newRows);
    }, [columns, rows, onChange]);

    const gridColumns: Column<Record<string, string>>[] = useMemo(() => {
        return columns.map((col, colIdx) => {
            if (readOnly) {
                const sortEntry = sorts.find((s) => s.column === col.name);
                const sortIdx = sortEntry ? sorts.indexOf(sortEntry) : -1;
                return {
                    ...keyColumn(col.name, stringColumn),
                    title: (
                        <div className="flex w-full items-center gap-0.5">
                            <span className="min-w-0 flex-1 truncate text-[13px] font-semibold">
                                {col.name}
                            </span>
                            {sortEntry && (
                                <span className="shrink-0 text-indigo-500">
                                    {sortEntry.direction === "asc" ? (
                                        <SortAscIcon size={12} />
                                    ) : (
                                        <SortDescIcon size={12} />
                                    )}
                                </span>
                            )}
                            {columnFilters.has(col.name) && (
                                <span className="shrink-0 text-amber-500">
                                    <FilterIcon size={12} />
                                </span>
                            )}
                            <ReadOnlyColumnHeaderMenu
                                col={col}
                                sortDirection={sortEntry?.direction ?? null}
                                sortIndex={sortIdx >= 0 ? sortIdx : null}
                                onSort={(dir) => handleSort(col.name, dir)}
                                filter={columnFilters.get(col.name) ?? null}
                                onFilterChange={(f) => handleFilterChange(col.name, f)}
                            />
                        </div>
                    ),
                    disabled: true,
                };
            }

            return {
                ...keyColumn(col.name, stringColumn),
                title: (
                    <div className="flex w-full items-center gap-0.5">
                        <ColumnHeaderInput
                            name={col.name}
                            onCommit={(newName) => updateColumnName(colIdx, newName)}
                        />
                        <ColumnHeaderMenu
                            col={col}
                            colIdx={colIdx}
                            colCount={columns.length}
                            canRemove={columns.length > 1}
                            onChangeRole={updateColumnRole}
                            onChangeDataType={updateColumnDataType}
                            onRemove={removeColumn}
                            onMove={moveColumn}
                        />
                    </div>
                ),
                disabled: false,
            };
        });
    }, [
        columns,
        readOnly,
        sorts,
        columnFilters,
        handleSort,
        handleFilterChange,
        updateColumnName,
        updateColumnRole,
        updateColumnDataType,
        removeColumn,
        moveColumn,
    ]);

    const handleRowsChange = useCallback(
        (newRows: Record<string, string>[]) => {
            onChange(columns, newRows);
        },
        [columns, onChange],
    );

    const createRow = useCallback((): Record<string, string> => {
        const row: Record<string, string> = {};
        columns.forEach((col) => {
            row[col.name] = "";
        });
        return row;
    }, [columns]);

    if (columns.length === 0) {
        return (
            <div className="p-4 text-center">
                <p className="mb-3 text-gray-400">No data yet.</p>
                {!readOnly && (
                    <button
                        onClick={() => {
                            const defaultCols: ColumnDefinition[] = [
                                { name: "Label", role: "primary_key", dataType: "string" },
                                { name: "Value", role: "data", dataType: "number" },
                            ];
                            onChange(defaultCols, [{ Label: "", Value: "" }]);
                        }}
                        className="cursor-pointer rounded-md border border-gray-300 bg-gray-50 px-4 py-2"
                    >
                        Create Table
                    </button>
                )}
            </div>
        );
    }

    return (
        <div onPaste={handlePaste}>
            {readOnly && rows.length > 0 && (
                <ReadOnlyToolbar
                    searchQuery={searchQuery}
                    onSearchChange={setSearchQuery}
                    sorts={sorts}
                    columnFilters={columnFilters}
                    onClearAll={clearAll}
                    filteredCount={processedRows.length}
                    totalCount={rows.length}
                />
            )}

            {readOnly && viewMode !== "table" ? (
                displayRows.length === 0 && rows.length > 0 ? (
                    <div className="py-6 text-center text-[13px] text-gray-400">
                        No rows match your search/filter criteria.
                    </div>
                ) : (
                    <DataChart
                        columns={columns}
                        rows={displayRows}
                        chartType={viewMode === "line" ? "LINE" : "BAR"}
                    />
                )
            ) : readOnly && displayRows.length === 0 && rows.length > 0 ? (
                <div className="py-6 text-center text-[13px] text-gray-400">
                    No rows match your search/filter criteria.
                </div>
            ) : (
                <DynamicDataSheetGrid
                    value={displayRows}
                    onChange={handleRowsChange}
                    columns={gridColumns}
                    createRow={createRow}
                    lockRows={readOnly}
                    addRowsComponent={
                        readOnly
                            ? false
                            : ({ addRows }) => (
                                  <AddRowsWithColumn
                                      addRows={addRows}
                                      onAddColumn={addColumn}
                                      exportColumns={exportFilename ? columns : undefined}
                                      exportRows={exportFilename ? rows : undefined}
                                      exportFilename={exportFilename}
                                  />
                              )
                    }
                    height={Math.min(displayRows.length * 36 + 40, 500)}
                    stickyRightColumn={
                        readOnly
                            ? undefined
                            : {
                                  component: ({ deleteRow }: CellProps<Record<string, string>>) => (
                                      <button
                                          onClick={deleteRow}
                                          title="Delete row"
                                          className="cursor-pointer border-none bg-transparent px-1 text-sm leading-none text-red-500 opacity-50 hover:opacity-100"
                                      >
                                          x
                                      </button>
                                  ),
                              }
                    }
                />
            )}

            {readOnly && rows.length > 0 && (
                <div className="mt-2 flex items-center gap-2">
                    <ViewModeToggle viewMode={viewMode} onChange={setViewMode} />
                    {exportFilename && (
                        <div className="ml-auto">
                            <ExportDropdown
                                columns={columns}
                                rows={displayRows}
                                filename={exportFilename}
                            />
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};
